/**
 * Meta (Facebook) Pixel helper.
 *
 * The base snippet in index.html defines `window.fbq` (a queueing stub
 * until fbevents.js loads) and calls `fbq('init', PIXEL_ID)`. This module
 * wraps it with types plus the handful of events we actually fire.
 *
 * Two deliberate choices mirror the Google Analytics setup:
 *
 * 1. Consent first. index.html calls `fbq('consent', 'revoke')` *before*
 *    init, so nothing leaves the browser until the cookie banner calls
 *    `grantMetaConsent()`. Events fired while revoked are queued by the
 *    pixel and flushed on grant, so a visitor who submits the form and
 *    then accepts still counts.
 * 2. No automatic PageView. The snippet does not call
 *    `fbq('track', 'PageView')`; RouteTracker fires it on every SPA route
 *    change instead, so Meta sees each navigation exactly once.
 */

export const META_PIXEL_ID = '1291570188568931';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fbq?: ((...args: any[]) => void) & { callMethod?: (...args: unknown[]) => void };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _fbq?: any;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fbq(...args: any[]): void {
  if (typeof window === 'undefined' || !window.fbq) return;
  // eslint-disable-next-line prefer-spread
  window.fbq.apply(null, args);
}

/**
 * A per-event ID so the same conversion sent twice — once from the
 * browser, once from the server via the Conversions API — is deduplicated
 * by Meta instead of double-counted. Falls back to a random string on
 * browsers without crypto.randomUUID.
 */
export function newEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function trackMetaPageView(path: string): void {
  fbq('track', 'PageView', { page_path: path });
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  const value = match?.[1];
  return value === undefined ? undefined : decodeURIComponent(value);
}

export interface PixelCookies {
  /** Meta's browser ID, set by the pixel once consent is granted. */
  fbp?: string;
  /** Meta's click ID, present when the visit came from a Meta ad. */
  fbc?: string;
}

/**
 * Reads the pixel's own cookies so they can be forwarded to the server
 * for the Conversions API call. These are what let Meta tie the
 * server-side event back to the same browser — without them, server
 * events match far less often.
 *
 * Both are absent until the visitor accepts the banner, since the pixel
 * sets nothing while consent is revoked.
 */
export function readPixelCookies(): PixelCookies {
  return { fbp: readCookie('_fbp'), fbc: readCookie('_fbc') };
}

/** Extra, non-identifying context attached to the Lead conversion. */
export interface LeadEventParams {
  /** Which form produced the lead, e.g. 'contact' or 'projectflow'. */
  source?: string;
  /** Budget bucket the visitor picked, if any. */
  budget?: string;
  /** Timeline bucket the visitor picked, if any. */
  timeline?: string;
  /**
   * Dedup key. Pass the same ID that was sent to our server for the
   * Conversions API call so Meta counts one conversion, not two. Omit to
   * have one generated (browser-only event).
   */
  eventId?: string;
}

/**
 * Fires the standard `Lead` conversion. This is the event Meta campaigns
 * optimise against, so it must fire only on a confirmed successful
 * submission — never on click or on a validation failure.
 *
 * No personal data (name, email, phone, brief text) is ever passed: only
 * the coarse buckets below, which are safe to use for custom conversions
 * and audience rules.
 *
 * Returns the event ID actually used, so the caller can confirm the
 * browser and server halves of the conversion agree.
 */
export function trackLead(params: LeadEventParams = {}): string {
  const eventId = params.eventId ?? newEventId();
  const payload: Record<string, string> = {
    content_name: params.source ?? 'lead_form',
    content_category: 'lead',
  };
  if (params.budget) payload.lead_budget = params.budget;
  if (params.timeline) payload.lead_timeline = params.timeline;

  fbq('track', 'Lead', payload, { eventID: eventId });
  return eventId;
}

/**
 * Consent Mode equivalent for the pixel. The banner calls these; the
 * default state set in index.html is revoked.
 */
export function grantMetaConsent(): void {
  fbq('consent', 'grant');
}

export function revokeMetaConsent(): void {
  fbq('consent', 'revoke');
}
